<template>
  <div class="p-4">
    <b-tabs card pills vertical>
      <b-tab title="About">
        <h3>About</h3>
        <h4>sci-fund EU</h4>
        <p>
          This is the sci-fund EU platform which provides intelligent search,
          semantic discovery, and visual exploration of European funding
          opportunities. It contains the whole Horizon Europe Framework
          Programme, as well as most calls from the last H2020 work programmes
          (2014-2020).
        </p>

        <h4 class="mt-5">Features</h4>
        <ul>
          <li>Access to current and forthcoming funding opportunities</li>
          <li>Sophisticated search with semantic features</li>
          <li>Visual exploration of the knowledge graph</li>
          <li>Search history and saved searches</li>
          <li>Search alerts</li>
          <li>Exporting search results to PDF, MS Word and Excel</li>
          <li>Sharing search results per email</li>
          <li>Access to infographics</li>
          <li>and more</li>
        </ul>
        <p>
          <a href="https://www.sci-fund.ai" target="_blank">Find out more</a>
          about sci-fund's features on our website.
        </p>

        <p>
          Are you ready to subscribe? Choose a
          <a href="https://www.sci-fund.ai/plans-and-prices" target="_blank"
            >fitting plan</a
          >
          for you or your organization! Or get your
          <a href="https://www.sci-fund.ai/trial-registration" target="_blank"
            >free trial account</a
          >
          today!
        </p>

        <h4 class="mt-5">Additional packages available on request for</h4>
        <ul>
          <li>
            Custom organization-specific templates for exporting and sharing
            results
          </li>
          <li>
            Custom organization-specific branding and sci-fund subdomain (e.g.,
            your-org.sci-fund.ai)
          </li>
          <li>Additional sources (such as national funding schemes)</li>
          <li>and much more</li>
        </ul>

        <p>
          For feedback, questions and comments, please contact us at
          <a href="mailto:info@sci-fund.ai">info@sci-fund.ai</a>.
        </p>
      </b-tab>
      <b-tab title="1. Searching and filtering">
        <h3>1. Searching and filtering</h3>
        <p>
          When logging in to sci-fund, the main page appears, namely the search
          page. The main parts of this page are:
        </p>
        <ul>
          <li>
            the free text search box at top of the page, which allows you to
            enter the term(s) you want to search for,
          </li>
          <li>the results, positioned in the main part of the page,</li>
          <li>
            the filters on the left side and the additional Deadline filter
            right above the results,
          </li>
          <li>
            and buttons that offer additional functionality above the Deadline
            filter.
          </li>
        </ul>

        <h4>Searching</h4>
        <p>
          Sci-fund offers many different ways to find what you are looking for.
          You can either enter some free text into the search box (e.g.
          "sustainability") or you can use the filters right away if you want to
          narrow down the entire set of topics by certain criteria. In this
          section we'll focus on the first one, the free text search.
        </p>
        <p>
          Suppose you start typing "sustainability". With every letter you type,
          sci-fund will try to autocomplete the terms for you by providing
          fitting suggestions within the drop-down menu. You can click on any of
          these to start the search or you can continue typing and click on the
          <b>Search</b> button or press <b>Enter</b> to start the search. The
          dropdown menu shows you also the top topics for the autocompleted
          search term along with some metadata.
        </p>

        <h4>Results</h4>
        <p>
          The results of your search appear on the main part of the screen. Each
          result represents a topic and contains the Topic Title, a passage from
          the document that's relative to your search, if it's Open, Closed or
          Forthcoming and some additional metadata such as the Call ID and the
          Deadline(s). There are also two buttons on the right side of each
          result,
          <b>Graph</b> and <b>Details</b>, about which we will talk in Sections
          2, 4.
        </p>
        <p>
          The results can be sorted by Relevancy, Title (ascending/descending)
          or Date (ascending/descending) by using the <b>Sorting by</b> button
          above the deadline filter.
        </p>

        <h4>Filters</h4>
        <p>
          As already mentioned in Searching, you can apply multiple filters to
          your search and also check the box
          <b>Preserve filters for next search</b> in order to save them for your
          consecutive search. There are various filter categories, such as
          Submission Status, Action Type, Budget, Technology Readiness Level,
          Opening date and many more. You can combine the filters however you
          like in order to drill down your search results. By removing either
          one or all selected filters, you broaden again the results you see.
        </p>
        <p>
          The number next to each filter option shows the total number of
          results that will be in the result list after the filter is applied.
        </p>

        <h4>Additional Functionality</h4>
        <p>
          In order to keep track of the results of your searches, you can use
          the buttons <b>Export</b> and <b>Share</b> right above the deadline
          filter. When pressing the <b>Export</b> button, a pop-up window opens
          which allows you to select the type of export: Word, Excel or PDF.
          More details can be found under: 2. Topic Export
        </p>
        <p>
          You can also share the results of your search with someone by
          providing an email address and then pressing <b>Send</b>. By ticking
          the box <b>send a copy to myself</b>, a copy of this email will be
          sent to the email address associated with your sci-fund account.
        </p>
      </b-tab>

      <b-tab title="2. Topic Export">
        <h3>2. Topic Export</h3>
        <p>
          There exist three different options to export topics from sci-fund:
          PDF, Word, and Excel.
        </p>
        <h4>PDF export</h4>
        <p>
          The PDF export allows to download the top x results (default: 100) in
          relation to a specific search including all set filters. You can
          change the number of results you want to include within the PDF. When
          pressing <b>Download</b>, the file will be generated and automatically
          downloaded. The exported PDF file contains a preview of the top x
          search results including a link leading back to all search results on
          sci-fund as well as links to the individual topics' detail pages.
        </p>
        <h4>Word export</h4>
        <p>
          The Word export allows to download the topics in Word format. When
          pressing <b>Download</b>, the file will be generated and automatically
          downloaded. The topics are structured by Programme division up to
          level 2; then Destination; and then Call. Topics without destination
          or call will be included within a dedicated section. The exported
          fields are predefined and include a link to the topic's page on the EU
          portal.
        </p>
        <p>
          The export takes both search query and filters into consideration.
          This means, it is on the one hand possible to export all topics, as
          well as only "open" topics or topics for a specific search term.
        </p>
        <p>
          Each organization can define their own Word template in the sci-fund
          administration interface.
        </p>
        <h4>Excel export</h4>
        <p>
          The Excel export allows to download the topics in Excel format. The
          exported fields are predefined and include a link to the topic's page
          on the EU portal. The export takes both search query and filters into
          consideration. This means, it is on the one hand possible to export
          all topics, as well as only "open" topics or topics for a specific
          search term.
        </p>
      </b-tab>

      <b-tab title="3. Topic Detail View">
        <h3>3. Topic Detail View</h3>
        <p>
          The <b>Details</b> button at the right of each topic leads to a pop-up
          window, which contains various information regarding the specific
          topic, that might be of interest to you. The pop-up window consists of
          six / seven tabs: <b>Overview</b>, <b>Conditions</b>, [<b
            >Destination Details</b
          >], <b>Support Info</b>, <b>Latest Info</b>,
          <b>Extracted Information</b>, <b>More like this</b>.
        </p>

        <h4>Overview</h4>
        <p>
          This tab contains the details of this topic, such as Publication Date,
          Call Title, Budget, Description, information about the Actions and a
          lot more. Furthermore, it links to the original source of information
          (EU Funding and Tender portal, etc.)
        </p>

        <h4>Conditions</h4>
        <p>
          In this tab, you can see the types of conditions applied to this call
          / topic, with links to the more detailed original sources of
          information. A selection of titles of general interest for the
          prospective applicants are listed under "Additional Documents"
          directing users to the corresponding files.
        </p>

        <h4>Destination Details</h4>
        <p>
          The Destination Details tab is available only to topics under the
          Horizon Europe Framework Programme and provides details about the
          destination under which the selected topic lies.
        </p>

        <h4>Support Info</h4>
        <p>
          Under this tab you can find contact points for further questions and
          assistance.
        </p>

        <h4>Latest Info</h4>
        <p>
          In this tab you can find the latest information added regarding the
          selected topic (as well as everything added after its original
          publication) along with the updates' / additions' dates.
        </p>

        <h4>Extracted Information</h4>
        <p>
          Each topic is connected to Semantic Concepts, which have been
          automatically extracted from the topic's information and these
          concepts are available in the tab <b>Extracted Information</b>. For
          each concept, the Link to the corresponding Wikidata entry and it's
          Definition are shown. There is also the possibility to change to the
          <b>Graph</b> view of the selected concept which shows the connections
          of this concept to other concepts and topics.
        </p>

        <h4>More like this</h4>
        <p>
          In the last tab, you can see other topics that are semantically
          related to the topic you are looking at and might therefore be of
          interest to you. If you select a topic title, you will be redirected
          to the Topic Detail View window of this topic.
        </p>

        <p>
          The <b>Graph</b> button next to each topic's title reveals the
          relationship of this topic to all the concepts that have been
          extracted through semantic analysis.
        </p>
      </b-tab>

      <b-tab title="4. Saved Searches and Alerts">
        <h3>4. Saved Searches and Alerts</h3>
        <p>
          When performing a search, you can save it by pressing the
          <b>Bookmark</b> button on the right side of the free text search box
          in order to easily research at a later point in time. It preserves all
          the filters you might have set. So, you can save a search query
          several times with different filters or even without query string but
          only set filters. The saved searches can be accessed by clicking on
          <b>Saved</b> in the top navigation menu. At the lower part of this
          screen, you find the list of your saved searches. You can research by
          clicking on the <b>Magnifying Glass</b> on the left of the
          corresponding list entry. If you <b>Select</b> it, the details of the
          saved search will appear in the dialogue on the upper part of the
          screen, where you can change the settings, e.g. what type of alert you
          wish: none/daily/weekly or monthly. Sci-fund will notify you via email
          about any changes on the results of your saved search. With
          <b>Save</b> you confirm your changes. Finally, you can delete a saved
          search by clicking on the <b>Delete</b> button.
        </p>

        <p>
          Apart from the possibility to save a search, you can also go back to
          your last searches using the <b>Clock</b> button on the left side of
          the free text search box. You will see a list of your last search
          terms from which you can trigger easily a new search. With
          <b>Delete</b> you can delete your entire search history.
        </p>
      </b-tab>
      <b-tab title="5. Exploration / Graph View">
        <h3>5. Exploration / Graph View</h3>
        <p>
          Sci-fund offers a graph view, that enables you to explore the
          connections between topics and concepts by selecting specific nodes of
          the graph and performing some actions on them.
        </p>
        <p>
          The view of the graph depends on the context (search, topic, concept)
          from where you entered the graph view by clicking the
          <b>Graph</b> icon. The graph may contain multiple topics and concepts
          or it focuses on a specific topic or a specific concept, showing its
          semantically related concepts / topics. The behaviour in a bit more
          detail:
        </p>
        <ul>
          <li>
            If you click on the <b>Graph</b> button next to the
            <b>Search</b> box, you will see all the resulting topics of your
            search in the graph, along with their semantically related concepts.
          </li>
          <li>
            If you click on the <b>Graph</b> button of a specific topic (through
            the initial Search window using the <b>Graph</b> button next to the
            <b>Details</b> button, or the <b>Graph</b> icon that appears next to
            the topic's name on the <b>Details</b> pop-up window or the same
            icon associated with a topic on the <b>More like this</b> tab), you
            will see this topic at the center of the graph with the related
            concepts surrounding it.
          </li>
          <li>
            If you click on the <b>Graph</b> icon associated with a specific
            concept on the <b>Extracted Information</b> tab of the
            <b>Details</b> pop-up window, then you will be redirected to a view
            of the graph that shows this concept with all it's related topics
            and concepts.
          </li>
        </ul>
        <p>
          You can pick a concept or a topic by clicking on the node that
          represents it or through the <b>Select topic or concept</b> dropdown
          (which is searchable) on the right part of your screen. After you
          selected a node, more information regarding this topic or concept will
          appear on the right side of the page. It contains a link that
          redirects you either on the <b>Search</b> page with this topic as
          result, or to the wikidata page of the selected concept. You can also
          <b>Expand</b> the node in order to see more topics and concepts
          associated with the selected node or <b>Change to node</b>. Then you
          will see a new graph focusing on the selected node. If you press the
          <b>Reset</b> button on the top right part of your screen, the graph
          will show the initial view of the last node you have focused on.
        </p>
        <p>
          In order to see the topics that appear on the graph in more detail,
          you can press the button <b>Show all topics in search</b>, which
          redirects you to the <b>Search</b> page showing all these topics as
          the results of an automatically generated search.
        </p>
        <p>There are some additional graph settings, you may change:</p>
        <ul>
          <li>
            <b>Show node labels</b> shows or hides the names of the nodes.
          </li>
          <li>
            <b>Font size</b> changes the font size of the nodes' labels to
            small, medium or large.
          </li>
          <li>
            <b>#topics</b> defines the number of topics that appear within the
            graph.
          </li>
          <li>
            <b>force</b> controls how dense the graph will be, e.g. how close
            the nodes will be to each other.
          </li>
        </ul>
      </b-tab>

      <b-tab title="6. Infographics">
        <h3>6. Infographics</h3>
        <p>
          The tab <b>Infographics</b> in the top navigation menu presents
          interactive, graphical views of important information regarding the
          topics (deadlines, hierarchical view etc.) of Horizon Europe. For the
          time being it consists of three infographics:
        </p>
        <ul>
          <li>a Treemap about the Clusters / Destination / Calls,</li>
          <li>
            a Bubble Chart with Budgets and Deadlines per Programme Part and
          </li>
          <li>a Deadlines Calendar.</li>
        </ul>
        <h4>Clusters / Destination / Calls (Treemap)</h4>
        <p>
          This <b>treemap</b> helps you find out how large different portions of
          Horizon Europe are with respect to number of funding topics and allow
          you to navigate to the parts of most interest to you.
        </p>
        <p>
          If you hover over a part of the treemap, you can see the corresponding
          section of Horizon Europe and the number of topics that belong to this
          part of the treemap. If you click on a part of the treemap, you can
          drill down on the hierarchy of <b>Programme Divisions</b> levels. By
          clicking on the lowest level, you will be redirected to the
          <b>Search</b> page, with the Programme Division filter activated and
          equal to the Programme Division that corresponds to the part of the
          treemap you clicked on. If you wish to go back to the Treemap, you
          should select again the <b>Infographics</b> tab or go back to the
          previous page.
        </p>
        <h4>Budgets and Deadlines per Programme Part (Bubble Chart)</h4>
        <p>
          The <b>bubble chart</b> gives you a sense of how much funding is
          available in different work areas, and how it's distributed over the
          lifetime of Horizon Europe.
        </p>
        <p>
          In this infographic you can see four sub-charts; one for each
          Programme Division / work area of Horizon Europe. The bubbles in each
          of these charts represent the deadlines of various topics of the
          selected work area and their size shows the total funding of the
          topics that belong to this category. When you hover over a bubble, you
          see the exact budget in millions of Euros and the month of the
          deadline. You can also see the number of topics that belong to this
          category and their titles (or at least some of them, if they are more
          than 10).
        </p>
        <h4>Deadlines Calendar (Temporal Heatmap)</h4>
        <p>
          Our third chart is the one that can help you make a better planning of
          your work and the topics you will focus on. The
          <b>Deadlines Calendar</b> shows you how many and what topics have
          deadlines on what dates.
        </p>
        <p>
          The size of the circles on each date represents the number of
          deadlines on this date and when you hover over a circle you can see
          the exact number of topics and the titles of all or some of them (in
          case there are more than 10).
        </p>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
export default {
  name: "HelpContent",
  computed: {},
};
</script>

<style scoped></style>
